import React, { useState, useEffect, useRef } from "react";

import { strings } from "@perceptron/constants";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@perceptron/ui/components/sheet";
import { Button } from "@perceptron/ui/components/button";
import { Input } from "@perceptron/ui/components/input";
import { cn } from "@perceptron/utils/cn";

interface RenameLabelSheetProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  currentLabel: string;
  onRename: (newLabel: string) => void;
}

export function RenameLabelSheet({ isOpen, onOpenChange, currentLabel, onRename }: RenameLabelSheetProps) {
  const [newLabel, setNewLabel] = useState(currentLabel);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout | undefined;

    if (isOpen) {
      setNewLabel(currentLabel);
      // Delay focus to allow sheet animation to complete
      timeoutId = setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      }, 300);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [isOpen, currentLabel]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedLabel = newLabel.trim();
    if (trimmedLabel && trimmedLabel !== currentLabel) {
      onRename(trimmedLabel);
      onOpenChange(false);
    }
  };

  const handleCancel = () => {
    setNewLabel(currentLabel);
    onOpenChange(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      handleCancel();
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={onOpenChange}>
      <SheetContent
        className={cn(
          "h-auto rounded-t-2xl border-t border-white/10 bg-black/90 backdrop-blur-xl",
          "px-4 pb-8 pt-4",
          "focus:outline-none",
          "!z-[10002]", // Higher than AnnotationDialog's z-10001
        )}
        showCloseButton={false}
        side="bottom"
      >
        <SheetHeader className="sr-only">
          <SheetTitle>{strings.imageDialog.toolbar.rename}</SheetTitle>
          <SheetDescription>{strings.annotationSidebar.editLabel}</SheetDescription>
        </SheetHeader>

        <form className="space-y-4" onSubmit={handleSubmit}>
          <div className="space-y-2">
            <label className="text-sm font-medium text-white/80" htmlFor="rename-input">
              {strings.common.label}
            </label>
            <Input
              autoCapitalize="words"
              autoComplete="off"
              autoFocus
              className={cn(
                "h-12 w-full rounded-lg border-white/20 bg-white/10",
                "text-base text-white placeholder:text-white/40",
                "focus:border-white/40 focus:bg-white/15",
                "focus-visible:ring-1 focus-visible:ring-white/40",
              )}
              enterKeyHint="done"
              id="rename-input"
              inputMode="text"
              placeholder={currentLabel}
              ref={inputRef}
              spellCheck={false}
              type="text"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              onKeyDown={handleKeyDown}
            />
          </div>

          <div className="flex gap-2">
            <Button
              className={cn(
                "h-12 flex-1 rounded-lg",
                "bg-white/5 font-medium text-white/80",
                "hover:bg-white/10 hover:text-white",
              )}
              size="lg"
              type="button"
              variant="ghost"
              onClick={handleCancel}
            >
              {strings.common.cancel}
            </Button>
            <Button
              className={cn(
                "h-12 flex-1 rounded-lg",
                "bg-white font-medium text-black",
                "hover:bg-white/90",
                "disabled:cursor-not-allowed disabled:opacity-40",
              )}
              disabled={!newLabel.trim() || newLabel.trim() === currentLabel}
              size="lg"
              type="submit"
            >
              {strings.common.rename}
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}
